import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export default function HotelWeeklyBoard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "default", dir: "asc" });

  useEffect(() => {
    axios.get(`${API}/dashboard/hotel-weekly`, { headers: getAuthHeader() })
      .then((r) => setRows(r.data?.hotels || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const sortedRows = useMemo(() => {
    if (sort.key === "default") return rows; // server order: overdue first
    const val = (h) => {
      switch (sort.key) {
        case "hotel": return (h.hotel_name || "").toLowerCase();
        case "last": return h.last_delivery || "";
        case "next": return h.next_order || "";
        case "status": return h.overdue ? 1 : 0;
        default: return 0;
      }
    };
    const out = [...rows].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return out;
  }, [rows, sort]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const Sh = ({ label, k, className = "" }) => (
    <th
      className={`px-4 py-3 font-semibold cursor-pointer select-none whitespace-nowrap hover:underline ${sort.key === k ? "text-brand-navy" : ""} ${className}`}
      onClick={() => toggleSort(k)}
      data-testid={`hotel-weekly-sort-${k}`}
    >
      {label} {sort.key === k ? (sort.dir === "asc" ? "▲" : "▼") : <span className="text-neutral-300">↕</span>}
    </th>
  );

  const fmtDate = (d) =>
    d ? new Date(d.slice(0, 10) + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";

  return (
    <Card data-testid="hotel-weekly-board">
      <CardHeader>
        <CardTitle className="font-display text-xl text-brand-navy">Hotel Weekly Overview</CardTitle>
        <CardDescription className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">
          Last delivery · next pending order · 7-day re-order check
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No hotel orders yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left text-neutral-600 border-b">
                <tr>
                  <Sh label="Hotel" k="hotel" />
                  <Sh label="Last delivery" k="last" />
                  <Sh label="Next order" k="next" />
                  <Sh label="Status" k="status" />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((h) => (
                  <tr
                    key={h.hotel_id}
                    className={`border-b last:border-0 ${h.overdue ? "bg-red-50/60" : ""}`}
                    data-testid={`hotel-weekly-row-${h.hotel_id}`}
                  >
                    <td className="px-4 py-3 font-medium">{h.hotel_name}</td>
                    <td className="px-4 py-3">
                      {fmtDate(h.last_delivery)}
                      {h.days_since_last !== null && (
                        <span className={`ml-1 text-xs ${h.days_since_last > 7 ? "text-red-600 font-bold" : "text-muted-foreground"}`}>
                          ({h.days_since_last}d ago)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {h.next_order ? (
                        <span className="text-emerald-700">{fmtDate(h.next_order)}</span>
                      ) : (
                        <span className="text-neutral-400">none scheduled</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {h.overdue ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700" data-testid={`hotel-weekly-overdue-${h.hotel_id}`}>
                          ⚠ Over 7 days — no order
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                          OK
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
